import { Spin, Descriptions, Select, Button } from 'antd';
import { CheckOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useState, useEffect, useCallback } from 'react';
import { useToggle } from 'react-use';
import { UmsMember, UmsMemberReceiveAddressV1 } from 'types/ums';
import { getMemberList, getMemberReceiveAddressListV1 } from 'apis/ums';
import { OrderGenerateForMemberDto } from 'types/oms';
import { findLabelByValue } from 'commons/options';
import formatTime from 'utils/formatTime';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import i18n from '../../../i18n';
import LOCALS from '../../../commons/locals';
import CreateMemberDrawer from 'components/create-member-drawer';
import AddressAdd from 'pages/ums/member-view/address-add';
import { MEMBER_LEVEL_OPTION_LIST } from 'constant/ums-member';

interface Props {
  setPayload: (
    data: Partial<
      Pick<OrderGenerateForMemberDto, 'memberId' | 'receiveAddressId'>
    > & {
      receiveAddressCountry?: string;
    },
  ) => void;
}

const MemberSelect = ({ setPayload }: Props) => {
  const location = useLocation();
  const [loading, toggleLoading] = useToggle(false);
  const [memberData, setMemberData] = useState<UmsMember>();
  const [umsMemberList, setUmsMemberList] = useState<UmsMember[]>([]);
  const [addressList, setAddressList] = useState<UmsMemberReceiveAddressV1[]>(
    [],
  );
  const [checkAddress, setCheckAddress] = useState<number>(0);
  const [createMemberOpen, toggleCreateMemberOpen] = useToggle(false);
  const [addressOpen, setAddressOpen] = useState(false);
  const [editingAddress, setEditingAddress] =
    useState<UmsMemberReceiveAddressV1>();

  useEffect(() => {
    if (!memberData) return;

    getMemberReceiveAddressListV1(memberData.id).then(({ data }) => {
      setAddressList(data);

      if (data.length) {
        const current = data.find((d) => d.id === checkAddress);
        const t = current || data.find((d) => d.defaultStatus === 1) || data[0];
        setCheckAddress(t.id);
        setPayload({
          receiveAddressId: t.id,
          receiveAddressCountry: t.province,
        });
      }
    });
  }, [checkAddress, memberData, setPayload]);

  const handleSearch = useCallback(
    (keyword: string) => {
      keyword = keyword.trim();
      if (!keyword) {
        setUmsMemberList([]);
        return;
      }
      toggleLoading(true);
      getMemberList({ keyword, pageNum: 1, pageSize: 10 })
        .then((data) => {
          setUmsMemberList(data.data.list);
        })
        .catch()
        .finally(() => toggleLoading(false));
    },
    [toggleLoading],
  );

  useEffect(() => {
    if (location.state) {
      const { memberEmail } = location.state;
      handleSearch(memberEmail);
    }
  }, [location, handleSearch]);

  useEffect(() => {
    if (location.state && umsMemberList.length) {
      const { memberId } = location.state;
      const target = umsMemberList.find((i) => i.id === memberId);
      setMemberData(target);
      setPayload({ memberId: memberId });
    }
  }, [location, umsMemberList, setPayload]);

  return (
    <div className="p-4">
      <div className="mb-4 flex justify-between items-center">
        <Select
          size="large"
          placeholder={i18n.t(LOCALS.phone_or_email) || '手机号/邮箱'}
          className="w-full"
          filterOption={false}
          notFoundContent={loading ? <Spin size="small" /> : null}
          onSearch={handleSearch}
          showSearch
          onChange={(memberId: UmsMember['id']) => {
            const target = umsMemberList.find((i) => i.id === memberId);
            setPayload({
              receiveAddressId: undefined,
              receiveAddressCountry: undefined,
            });
            if (target) {
              setMemberData(target);
              setPayload({ memberId: target.id });
            }
          }}
          options={umsMemberList.map((i) => {
            return {
              ...i,
              value: i.id,
              label: `${i.showName}(${i.email})`,
            };
          })}
        ></Select>

        <Button
          size="large"
          type="primary"
          className="ml-4"
          onClick={toggleCreateMemberOpen}
        >
          {i18n.t(LOCALS.add)}
        </Button>
      </div>

      {memberData && (
        <Descriptions bordered size="small">
          <Descriptions.Item label={i18n.t(LOCALS.email) || '邮箱'}>
            <Link to={`/ums/member-view/${memberData.id}`} target="_blank">
              {memberData.email}
            </Link>
          </Descriptions.Item>
          <Descriptions.Item label={i18n.t(LOCALS.name) || '用户名'}>
            {memberData.showName}
          </Descriptions.Item>
          <Descriptions.Item
            label={i18n.t(LOCALS.available_points) || '可用积分'}
          >
            <div className="text-red-500 font-bold">
              {memberData.integration}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={i18n.t(LOCALS.phone_number) || '电话'}>
            {memberData.showPhone}
          </Descriptions.Item>
          <Descriptions.Item label={i18n.t(LOCALS.member_level) || '会员类型'}>
            {findLabelByValue(memberData.memberLevel, MEMBER_LEVEL_OPTION_LIST)}
          </Descriptions.Item>
          <Descriptions.Item
            label={i18n.t(LOCALS.registration_time) || '注册时间'}
          >
            {formatTime(memberData.createTime)}
          </Descriptions.Item>
        </Descriptions>
      )}

      {memberData && (
        <div className="mt-4 mb-3 flex justify-end">
          <Button
            type="primary"
            ghost
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingAddress(undefined);
              setAddressOpen(true);
            }}
          >
            {i18n.t('add_address')}
          </Button>
        </div>
      )}

      {!!addressList.length && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {addressList.map((item) => {
            const selected = checkAddress === item.id;
            return (
              <div
                key={item.id}
                onClick={() => {
                  setCheckAddress(item.id);
                  setPayload({
                    receiveAddressId: item.id,
                    receiveAddressCountry: item.province,
                  });
                }}
                className={classNames(
                  'flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors',
                  selected
                    ? 'border-[#1677ff] bg-[#f0f7ff]'
                    : 'border-gray-200 bg-white hover:border-[#91caff]',
                )}
              >
                <span
                  className={classNames(
                    'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px]',
                    selected
                      ? 'border-[#1677ff] bg-[#1677ff] text-white'
                      : 'border-gray-300 bg-white',
                  )}
                >
                  {selected && <CheckOutlined />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex flex-wrap items-center gap-x-2">
                    <span className="font-medium text-gray-900">
                      {item.name}
                    </span>
                    <span className="text-sm text-gray-500">
                      {item.phoneNumber}
                    </span>
                  </div>
                  <div className="text-sm leading-5 text-gray-600">
                    {item.postCode} {item.country?.name} {item.city?.name || ''}
                  </div>
                  <div className="mt-0.5 break-all text-sm leading-5 text-gray-800">
                    {item.detailAddress}
                  </div>
                </div>
                <Button
                  type="link"
                  size="small"
                  className="shrink-0 px-0"
                  icon={<EditOutlined />}
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditingAddress(item);
                    setAddressOpen(true);
                  }}
                >
                  {i18n.t(LOCALS.edit)}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <CreateMemberDrawer
        open={createMemberOpen}
        onClose={() => toggleCreateMemberOpen(false)}
        setUmsMember={(umsMember) => {
          setMemberData(umsMember);
          setPayload({ memberId: umsMember.id });
        }}
      />

      {addressOpen && memberData && (
        <AddressAdd
          title={
            editingAddress
              ? i18n.t(LOCALS.edit) || ''
              : i18n.t(LOCALS.add_address) || ''
          }
          address={editingAddress}
          open={true}
          onClose={() => {
            setAddressOpen(false);
            setEditingAddress(undefined);
          }}
          memberId={memberData?.id}
          getLoad={() => {
            setMemberData({ ...memberData });
            setPayload({ memberId: memberData.id });
          }}
        />
      )}
    </div>
  );
};

export default MemberSelect;
